import { getKeycloakConfig } from './keycloakConfig';
import { keycloakHttpRequest } from './keycloakHttp';

type AccountAttributes = Record<string, string[]>;

type AccountRepresentation = {
	attributes?: AccountAttributes;
	firstName?: string;
	lastName?: string;
	username?: string;
};

export type AccountAvatarProfile = {
	picture: string | null;
	initials: string;
};

function avatarsUrl(): string {
	const { url, realm } = getKeycloakConfig();
	const base = url.replace(/\/$/, '');

	return `${base}/realms/${realm}/apexx-avatars`;
}

function accountUrl(): string {
	const { url, realm } = getKeycloakConfig();
	const base = url.replace(/\/$/, '');

	return `${base}/realms/${realm}/account`;
}

function firstAttribute(attributes: AccountAttributes | undefined, name: string): string | null {
	const values = attributes?.[name];
	if (!values || values.length === 0) {
		return null;
	}
	const value = values[0]?.trim();
	return value ? value : null;
}

function initialsFromAccount(account: AccountRepresentation): string {
	const first = account.firstName?.trim() ?? '';
	const last = account.lastName?.trim() ?? '';
	if (first || last) {
		return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '?';
	}
	const username = account.username?.trim() ?? '';
	return username.slice(0, 2).toUpperCase() || '?';
}

/**
 * Loads the current user's avatar URL and initials from the Keycloak Account API.
 */
export async function fetchAccountAvatarProfile(
	accessToken: string,
): Promise<AccountAvatarProfile> {
	const response = await keycloakHttpRequest(accountUrl(), {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${accessToken}`,
			Accept: 'application/json',
		},
	});

	if (!response.ok) {
		throw new Error(
			response.status === 401
				? 'Keycloak account GET failed: 401'
				: `Keycloak account GET failed: ${response.status}`,
		);
	}

	const account = (await response.json()) as AccountRepresentation;

	return {
		picture: firstAttribute(account.attributes, 'picture'),
		initials: initialsFromAccount(account),
	};
}

/**
 * Upload (or replace) the authenticated user's avatar via the apexx-avatars SPI.
 * Sends the raw image bytes with {@code Content-Type: image/jpeg|png|webp}.
 */
export async function uploadOwnAvatar(accessToken: string, file: File): Promise<string> {
	const contentType = file.type || 'application/octet-stream';
	if (!['image/jpeg', 'image/png', 'image/webp'].includes(contentType)) {
		throw new Error('Avatar must be a JPEG, PNG, or WebP image');
	}

	const response = await fetch(avatarsUrl(), {
		method: 'POST',
		headers: {
			Authorization: `Bearer ${accessToken}`,
			Accept: 'application/json',
			'Content-Type': contentType,
		},
		body: file,
	});

	if (!response.ok) {
		let detail = '';
		try {
			const body = (await response.json()) as { error?: string };
			if (body.error) {
				detail = `: ${body.error}`;
			}
		} catch {
			/* ignore */
		}
		throw new Error(`Avatar upload failed: ${response.status}${detail}`);
	}

	const body = (await response.json()) as { picture?: string };
	if (!body.picture) {
		throw new Error('Avatar upload failed: missing picture URL');
	}

	return body.picture;
}

/**
 * Clear the authenticated user's avatar via the apexx-avatars SPI.
 */
export async function deleteOwnAvatar(accessToken: string): Promise<void> {
	const response = await fetch(avatarsUrl(), {
		method: 'DELETE',
		headers: {
			Authorization: `Bearer ${accessToken}`,
			Accept: 'application/json',
		},
	});

	if (!response.ok && response.status !== 204) {
		throw new Error(`Avatar delete failed: ${response.status}`);
	}
}
